import React, { useState, useEffect, useRef } from 'react';
import {
  VictoryChart,
  VictoryStack,
  VictoryBar,
  VictoryAxis,
  VictoryTooltip,
  VictoryLabel,
  VictoryLegend,
  VictoryGroup,
} from 'victory';
import { Heading, Icon, Button } from '../../common';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import styled from '@emotion/styled';
import 'react-loading-skeleton/dist/skeleton.css'
import copy_active from '../../images/copy_active.svg';
import { useInView } from 'react-intersection-observer';
import { Accordion } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp,FaChevronRight } from 'react-icons/fa';
import { AiOutlineCamera } from 'react-icons/ai';
import { toPng } from 'html-to-image';

const StyledAccordion = styled(Accordion)`
  display: block;
  border: none;
  background-color: transparent;

  .accordion-item {
    border: none;
    background-color: transparent;
  }

  .accordion-header {
    button {
      background-color: transparent;
      border: none;
      white-space: nowrap;
    }
  }
`;

const VerticalStackedRankedBarChart = ({ questionObject, qid, question, information, handleCopyClick, data, filters, options, questionID, isLoadingQuestion }) => {
  const colors = [
    'rgb(8, 48, 107)',    // Darkest blue
    'rgb(10, 72, 143)',
    'rgb(20, 96, 179)',
    'rgb(40, 120, 204)',
    'rgb(70, 145, 219)',
    'rgb(100, 170, 230)',
    'rgb(135, 195, 240)',
    'rgb(170, 215, 245)',
    'rgb(200, 230, 250)',
    'rgb(230, 245, 255)'  // Lightest blue
  ];
  const CustomButton = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: var(--midWhite);
	color: var(--sapGreen);
	border-radius: 3px;
	padding: 0.4rem 0.9rem;
	img {
		padding-right: 5px;
	}
	:hover {
		background: var(--midWhite);
		color: ${(props) => props.buttonColor};
	}

	:disabled,
	[disabled] {
		background: var(--midWhite);
		border: none;
		color: var(--middleGrey);

		:hover {
			background: var(--midWhite);
			border: none;
			color: var(--middleGrey);
			cursor: not-allowed;
		}
	}
`;
const [addReport,setAddReport] = React.useState({
  contribution: null, graphItem: null
})
const updateReport = (selection)=>{
  setAddReport(selection)
}
const [ref, inView] = useInView({
  triggerOnce: true, 
  threshold: 0.5,    
});

  // Update legend data to include composite scores
  const legendData = data.map((choice, index) => {
    const compositeScore = questionObject?.Composite?.find(c => c.id === choice.id)?.compositeScore || 'N/A';
    return {
      name: `Score: ${compositeScore} - ${choice.choiceName}`,
      symbol: { fill: colors[index] }
    };
  });

  // Restructure data for grouped bar chart
  const transformedData = [];
  
  // Calculate max count for consistent minimum bar height
  const allCounts = [];
  if (data.length > 0 && data[0].rankings) {
    for (let rankIndex = 0; rankIndex < data[0].rankings.length; rankIndex++) {
      data.forEach(choice => {
        allCounts.push(parseInt(choice.rankings[rankIndex]?.anscnt || 0, 10));
      });
    }
  }
  const maxCount = Math.max(...allCounts, 1);
  const minBarHeight = maxCount * 0.01; // 1.5% of max for visibility

  // First, create one category per rank (for x-axis)
  if (data.length > 0 && data[0].rankings) {
    for (let rankIndex = 0; rankIndex < data[0].rankings.length; rankIndex++) {
      data.forEach((choice, choiceIndex) => {
        const count = parseInt(choice.rankings[rankIndex]?.anscnt || 0, 10);
        transformedData.push({
          x: `Rank ${rankIndex + 1}`,
          y: count === 0 ? minBarHeight : count,
          anscnt: count, // Keep original count for reference
          choiceName: choice.choiceName,
          rankNumber: rankIndex + 1,
          label: `${choice.choiceName}: ${count}`
        });
      });
    }
  }

  const wrapText = (text, maxChars) => {
    if(!text) return [""];
    const words = text.split(" ");
    let lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        if (currentLine.length + words[i].length + 1 <= maxChars) {
            currentLine += " " + words[i];
        } else {
            lines.push(currentLine);
            currentLine = words[i];
        }
    }
    lines.push(currentLine);
    return lines;
  };

  // Update legend data to show choices with composite scores
  const choiceLegendData = data.map((choice, index) => {
    const compositeScore = questionObject?.Composite?.find(c => c.id === choice.id)?.compositeScore || 'N/A';
    return {
      name: wrapText(`Score: ${compositeScore} - ${choice.choiceName}`, 95),
      symbol: { fill: colors[index] }
    };
  });

  const [activeKey, setActiveKey] = useState(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [forceRender, setForceRender] = useState(0);
  const chartRef = useRef();

  const handleAccordionToggle = () => {
    setIsAccordionOpen(prev => !prev);
    setTimeout(() => {
      setForceRender(prev => prev + 1);
    }, 100);
  };

  useEffect(() => {
    if (isAccordionOpen) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [isAccordionOpen]);

  // Calculate dynamic height for chart to accommodate the legend
  const legendNumRows = Math.ceil(choiceLegendData.length / 2);
  const calculatedLegendHeight = legendNumRows * 40; 
  const dynamicHeight = Math.max(450, 380 + calculatedLegendHeight);
  const dynamicBottomPadding = dynamicHeight - 250;

  const handleExport = () => {
    const chartContainer = chartRef.current;

    if (chartContainer) {
      toPng(chartContainer, { 
        cacheBust: true,
        height: chartContainer.offsetHeight + 100,
        style: {
          padding: '10px 20px',
          backgroundColor: 'white',
          overflow: 'visible'
        },
        backgroundColor: 'white',
        quality: 1.0,
        pixelRatio: 2
      })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "chart.png";
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error("Error exporting chart as image:", err);
      });
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <StyledAccordion>
        <Accordion.Item eventKey="0">
          <div className='d-flex justify-content-between align-items-start'>
            <Accordion.Header>
              <div>
                <Heading
                  fontSize='14px'
                  fontWeight='700'
                  color='var(--deepBlue)'
                  className='mb-0 pb-0'
                  breakHeading={false}
                  onClick={() => {
                    setActiveKey(activeKey === '0' ? null : '0');
                    handleAccordionToggle();
                  }}
                >
                  {question}
                  {activeKey === '0' ? (
                    <FaChevronDown size={10} style={{ marginLeft: '8px' }} />
                  ) : (
                    <FaChevronRight size={10} style={{ marginLeft: '8px' }} />
                  )}
                  {information && (
                    <span>
                      <Heading fontSize='12px' fontWeight='400' color='var(--deepBlue)' className='mb-0 pb-0 pl-1'>
                        {information}
                      </Heading>
                    </span>
                  )}
                </Heading>
              </div>
            </Accordion.Header>
            <div>
              <CustomButton
                fontWeight='400'
                fontSize='10px'
                size='small'
                variant='outlined'
                buttonColor='var(--sapGreen)'
                onClick={() => handleCopyClick(qid, addReport, question, questionObject)}
              >
                <img src={copy_active} />
                {' '}
                Copy to Report
              </CustomButton>
            </div>
          </div>

          <Accordion.Body>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleExport}
                  style={{
                    padding: "0.3rem 1rem",
                    backgroundColor: "#2361a0",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "32px",
                    marginLeft: "40px"
                  }}
                >
                  <AiOutlineCamera style={{ fontSize: "1.5rem" }} />
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div 
                style={{ 
                  maxWidth: '1600px', 
                  width: '100%',
                  overflow: 'visible',
                  padding: '20px'
                }} 
                key={`chart-${forceRender}`} 
                ref={chartRef}
              >
                <VictoryChart
                  domainPadding={{ x: 50, y: 10 }}
                  height={dynamicHeight}
                  width={1100}
                  padding={{ top: 50, bottom: dynamicBottomPadding, left: 60, right: 60 }}
                  style={{
                    parent: {
                      overflow: 'visible'
                    }
                  }}
                >
                  <VictoryLegend
                    x={50}
                    y={370}
                    orientation="horizontal"
                    gutter={20}
                    symbolSpacer={5}
                    itemsPerRow={2}
                    rowGutter={{ top: 0, bottom: 10 }}
                    labelComponent={<VictoryLabel lineHeight={1.4} />}
                    style={{ 
                      labels: { 
                        fontSize: 10, 
                        dy: -1,
                        fill: 'var(--paraColor)'
                      },
                      fontFamily: 'Noto Sans'
                    }}
                    data={choiceLegendData}
                  />
                  <VictoryAxis
                    label="Ranks"
                    style={{
                      axis: { stroke: '#ccc' },
                      grid: { stroke: 'var(--grey20)', strokeDasharray: '5,5' },
                      tickLabels: { 
                        fontSize: 11, 
                        padding: 8,
                        fill: 'var(--paraColor)',
                        angle: -45,
                        textAnchor: 'end'
                      },
                      axisLabel: { 
                        fontSize: 12, 
                        padding: 85,
                        fill: 'var(--paraColor)'
                      },
                      fontFamily: 'Noto Sans'
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    label="Number of Users"
                    style={{
                      axis: { stroke: '#ccc' },
                      grid: { stroke: 'var(--grey20)', strokeDasharray: '5,5' },
                      tickLabels: { 
                        fontSize: 11, 
                        padding: 8,
                        fill: 'var(--paraColor)'
                      },
                      axisLabel: { 
                        fontSize: 12, 
                        padding: 40,
                        fill: 'var(--paraColor)'
                      },
                      fontFamily: 'Noto Sans'
                    }}
                  />
                  {/* Secondary axis for composite scores */}
                  <VictoryAxis
                    tickValues={data.map((_, i) => i)}
                    tickFormat={(t) => {
                      const choice = data[t];
                      if (!choice) return "";
                      const compositeScore = questionObject?.Composite?.find(c => c.id === choice.id)?.compositeScore || 'N/A';
                      return `Score: ${compositeScore}`;
                    }}
                    style={{
                      axis: { stroke: "transparent" },
                      tickLabels: { 
                        fontSize: 10, 
                        padding: 15,
                        fill: 'var(--deepBlue)',
                        fontWeight: 'bold'
                      }
                    }}
                    orientation="bottom"
                    offsetY={110}
                  />
                  {/* Grouped bar chart with joined bars */}
                  <VictoryGroup
                    offset={8}
                    alignment="start"
                    colorScale={colors.slice(0, data.length)}
                    style={{
                      data: {
                        stroke: "var(--white)",
                        strokeWidth: 1,
                        cursor: "pointer",
                      }
                    }}
                  >
                    {data.map((choice, choiceIndex) => (
                      <VictoryBar
                        key={choiceIndex}
                        data={transformedData.filter(d => d.choiceName === choice.choiceName)}
                        labels={({ datum }) => datum.label}
                        labelComponent={
                          <VictoryTooltip
                            style={{ fontSize: 10, fill: "var(--white)", fontFamily: 'Noto Sans' }}
                            flyoutStyle={{ 
                              fill: "var(--mediumGrey)", 
                              stroke: "var(--white)", 
                              strokeWidth: 1 
                            }}
                            cornerRadius={5}
                            pointerLength={10}
                          />
                        }
                        barWidth={8}
                        barRatio={0.9}
                      />
                    ))}
                  </VictoryGroup>
                </VictoryChart>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </StyledAccordion>
      <div style={{ borderBottom: '1px solid var(--greyBlue30)' }}></div>
    </div>
  );
};

export default VerticalStackedRankedBarChart;